class TabsComponent extends HTMLElement {
  connectedCallback() {
    if (this.tabs) return;
    this.tabs = [...this.querySelectorAll('[role="tab"]')].filter((tab) => tab.closest('tabs-component') === this);
    this.indicator = this.querySelector('[data-tabs-indicator]');
    this.tablist = this.querySelector('[role="tablist"]');
    if (!this.tabs.length) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });

    const initial = Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));
    this.select(initial, { focus: false, animate: false });

    this.resizeObserver = new ResizeObserver(() => this.moveIndicator());
    this.resizeObserver.observe(this);

    if (window.Shopify?.designMode) {
      this.onBlockSelect = (event) => {
        const index = this.tabs.findIndex((tab) => tab === event.target || tab.contains(event.target));
        if (index > -1) this.select(index, { focus: false });
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  panelFor(tab) {
    return document.getElementById(tab.getAttribute('aria-controls'));
  }

  select(index, { focus = true, animate = true } = {}) {
    const next = this.tabs[index];
    if (!next) return;
    if (animate && next.getAttribute('aria-selected') === 'true') {
      if (focus) next.focus();
      return;
    }
    if (animate) this.lockHeight();
    this.tabs.forEach((tab) => {
      const active = tab === next;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      const panel = this.panelFor(tab);
      if (!panel) return;
      if (active) {
        this.hydrate(panel);
        panel.hidden = false;
        if (animate) {
          panel.classList.remove('is-entering');
          void panel.offsetWidth;
          panel.classList.add('is-entering');
        }
      } else {
        panel.hidden = true;
        panel.classList.remove('is-entering');
      }
    });
    if (focus) next.focus();
    this.moveIndicator();
    if (animate) window.Theme?.ss?.('feat', 'tab_open', { i: index, label: next.textContent.replace(/\s+/g, ' ').trim().slice(0, 40) });
    this.dispatchEvent(new CustomEvent('tabs:change', { detail: { index }, bubbles: true }));
  }

  lockHeight() {
    clearTimeout(this.unlockTimer);
    this.style.minHeight = `${this.offsetHeight}px`;
    this.unlockTimer = setTimeout(() => this.style.removeProperty('min-height'), 650);
  }

  hydrate(panel) {
    const template = panel.querySelector(':scope > template[data-lazy-panel]');
    if (!template) return;
    panel.append(template.content.cloneNode(true));
    template.remove();
    window.Theme?.initReveal?.();
  }

  moveIndicator() {
    if (!this.indicator || !this.tablist) return;
    const active = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (!active) return;
    const listRect = this.tablist.getBoundingClientRect();
    const rect = active.getBoundingClientRect();
    this.indicator.style.setProperty('--indicator-left', `${rect.left - listRect.left + this.tablist.scrollLeft}px`);
    this.indicator.style.setProperty('--indicator-width', `${rect.width}px`);
    this.indicator.classList.add('is-ready');
    if (this.tablist.scrollWidth > this.tablist.clientWidth) {
      const left = active.offsetLeft - (this.tablist.clientWidth - active.offsetWidth) / 2;
      this.tablist.scrollTo({ left, behavior: 'smooth' });
    }
  }

  onKeydown(event, index) {
    const last = this.tabs.length - 1;
    const map = { ArrowRight: index === last ? 0 : index + 1, ArrowLeft: index === 0 ? last : index - 1, Home: 0, End: last };
    if (!(event.key in map)) return;
    event.preventDefault();
    this.select(map[event.key]);
  }
}

if (!customElements.get('tabs-component')) customElements.define('tabs-component', TabsComponent);
